import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import ConsoleCard from './ConsoleCard';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import ConfirmDialog from '../common/ConfirmDialog';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from '../TranscriptSliderArea/useTranscriptMessages.hooks';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Transcript section: heading + inner white card. The grey card backdrop is
  // the shared left-pane card (MainPageLayout), so this stays transparent. The
  // takeover expansion is applied to that card (not this section), so the
  // reminder + AI status sections stay visible alongside the transcript.
  section: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // The transcript message stream, below the header divider (supplied by
  // ConsoleCard). The body just adds the matching top breathing room below that
  // line, and horizontal room for the bubbles' tails.
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Horizontal room for the bubbles' protruding WhatsApp tails (they extend
    // ~6px past the bubble edge); overflowX hidden so that never shows an
    // x-scrollbar. Clipping is at the padding box, so the tails stay visible.
    overflowX: 'hidden',
    paddingLeft: spacing(1),
    paddingRight: spacing(1),
    paddingTop: spacing(1.25),
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(1),
  },
  divider: {
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.guardsmanRed,
  },
  underline: {
    flex: 1,
    padding: '0.75px',
    backgroundColor: Colors.guardsmanRed,
    margin: '5px',
    display: 'inline-block',
  },
}));

/**
 * LIVE TRANSCRIPT (left pane). A single card: header (chat icon + label +
 * accordion chevron) above an inset divider, then the AI/customer message
 * stream (supervised-agent divider + click-to-report ASR-missing flow).
 * Collapsing via the chevron hides the stream, leaving just the header.
 *
 * NOTE: the volume control has been moved out of here for now.
 */
interface LiveTranscriptCardProps {
  // The takeover expansion lives on the parent left card; this card only needs
  // to know whether to show its collapse control.
  isExpanded?: boolean;
  onCollapse?: () => void;
  // Accordion collapse, mirroring Customer Pulse / Action Center. Controlled
  // (open + onOpenChange) so the parent can sync the resizable panel; falls back
  // to internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LiveTranscriptCard = ({
  isExpanded,
  onCollapse,
  open: openProp,
  onOpenChange,
}: LiveTranscriptCardProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);

  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') || target.closest('[role="dialog"]'))
      ) {
        return;
      }
      setConfirmDialogOpen(true);
    },
    []
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });
    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  return (
    <div className={classes.section}>
      <ConsoleCard
        icon={<ChatBubbleOutlineOutlinedIcon />}
        title="Transcript"
        open={open}
        onToggle={toggleOpen}
        py={1.25}
        px={1.25}
        // Keep the message stream mounted (hidden) so collapse/expand is instant
        // and messages aren't re-rendered from scratch on every expand.
        keepBodyMounted
        // During takeover, the chevron is swapped for a Close button that lifts
        // the card back out of the overlay.
        headerAction={
          isExpanded ? (
            <IconButton
              aria-label="Collapse transcript"
              size="small"
              onClick={onCollapse}
              // sx (not makeStyles) so it beats MUI ButtonBase's position: relative.
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                p: 0.25,
                zIndex: 1,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      >
        <div className={classes.messages} onClick={handleTranscriptAreaClick}>
          {sessionBufferMessagesList.length > 0 && (
            <MessageContainer messageList={sessionBufferMessagesList} compact />
          )}
          {currentTaskAIStatus && sessionBufferMessagesList.length > 0 && (
            <div className={classes.dividerContainer}>
              <div className={classes.underline} />
              <div className={classes.divider}>With Supervised Agent</div>
              <div className={classes.underline} />
            </div>
          )}
          <MessageContainer messageList={messagesList} compact />
        </div>
      </ConsoleCard>
      <ConfirmDialog
        open={confirmDialogOpen}
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default LiveTranscriptCard;
